import React, { useState, useRef, useEffect } from "react";
import CalendarIcon from "../../../../assets/svg/calendarIcon";

interface CustomDatePickerProps {
  value: string;
  onChange: (value: string) => void;
  hasError?: boolean;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  hasError = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [selectedAmPm, setSelectedAmPm] = useState<'AM' | 'PM'>('AM');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize from value
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setCurrentMonth(date);
      
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      hours = hours % 12;
      if (hours === 0) hours = 12;
      
      setSelectedHour(hours);
      setSelectedMinute(minutes);
      setSelectedAmPm(ampm);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDisplayValue = () => {
    if (!selectedDate) return '';
    
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const year = selectedDate.getFullYear();
    const hour = String(selectedHour).padStart(2, '0');
    const minute = String(selectedMinute).padStart(2, '0');
    
    return `${month}/${day}/${year} - ${hour}:${minute}`;
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentMonth(date);
  };

  const handleOkClick = () => {
    if (selectedDate) {
      let hours = selectedHour;
      if (selectedAmPm === 'PM' && hours !== 12) hours += 12;
      if (selectedAmPm === 'AM' && hours === 12) hours = 0;
      
      const finalDate = new Date(selectedDate);
      finalDate.setHours(hours, selectedMinute, 0, 0);
      
      // Format to datetime-local format
      const year = finalDate.getFullYear();
      const month = String(finalDate.getMonth() + 1).padStart(2, '0');
      const day = String(finalDate.getDate()).padStart(2, '0');
      const hour = String(finalDate.getHours()).padStart(2, '0');
      const minute = String(finalDate.getMinutes()).padStart(2, '0');
      
      onChange(`${year}-${month}-${day}T${hour}:${minute}`);
      setIsOpen(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentMonth(newMonth);
  };

  const isSameMonth = (date: Date) => {
    return date.getMonth() === currentMonth.getMonth();
  };

  const isSelected = (date: Date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const amPmOptions = ['AM', 'PM'];

  return (
    <div className="d-flex align-items-center gap-2 w-50" ref={dropdownRef}>
      <div style={{ position: 'relative', width: '100%' }}>
        <input
          type="text"
          className={`form-control bg-white ${hasError ? "is-invalid" : ""}`}
          value={formatDisplayValue()}
          placeholder="MM/DD/YYYY - hh:mm"
          readOnly
          onClick={() => setIsOpen(!isOpen)}
          style={{
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: '0.5rem 0.75rem',
            border: '1px solid #ced4da',
            borderRadius: '0.375rem',
          }}
        />
        
        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1050,
              backgroundColor: 'white',
              border: '1px solid #5376F0',
              borderRadius: '1.25rem',
              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
              padding: '1rem',
              paddingRight: '0rem',
              paddingLeft: '0rem',
              width: '520px',
              marginTop: '4px'
            }}
          >
            <div className="d-flex" style={{ borderBottom: '1px solid #dee2e6' }}>
              {/* Calendar Section */}
              <div style={{ flex: 1, marginRight: '1rem', borderBottom: '1px solid #dee2e6' }}>
                {/* Month Navigation */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 px-4" style={{ color: '#26397E', fontWeight: 600, fontSize: '1rem' }}>
                    {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h6>
                  <button
                    type="button"
                    onClick={() => navigateMonth('prev')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                  >
                    ‹
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => navigateMonth('next')}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      color: '#6c757d'
                    }}
                  >
                    ›
                  </button>
                </div>

                {/* Day Headers */}
                <div className="d-flex mb-2">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        textAlign: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        color: '#26397E',
                        padding: '0.25rem'
                      }}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div>
                  {Array.from({ length: 6 }, (_, weekIndex) => (
                    <div key={weekIndex} className="d-flex">
                      {getDaysInMonth(currentMonth).slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => (
                        <button
                          key={dayIndex}
                          type="button"
                          onClick={() => handleDateSelect(date)}
                          style={{
                            flex: 1,
                            height: '40px',
                            border: isSelected(date) ? '1px solid #5376F0' : 'none',
                            backgroundColor: isSelected(date) ? '#A7CCFF' : 'transparent',
                            color: isSelected(date) ? '#333' : isSameMonth(date) ? '#333' : '#ccc',
                            borderRadius: '100%',
                            margin: '1px',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {date.getDate()}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Selection Section */}
              <div style={{ width: '180px', borderLeft: '1px solid #dee2e6', marginTop: '-1rem' }}>
                <div className="d-flex" style={{ height: '350px' }}>
                  {/* Hours */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        height: '100%',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRight: 'none',
                        borderTop: 'none',
                        // borderRadius: '0.25rem',
                        padding: '0.25rem',
                        marginRight: '0rem'
                      }}
                    >
                      {hours.map((hour) => (
                        <div
                          key={hour}
                          onClick={() => setSelectedHour(hour)}
                          style={{
                            padding: '0.5rem',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedHour === hour ? '#26397E' : 'transparent',
                            color: selectedHour === hour ? 'white' : '#333',
                            borderRadius: '0.25rem',
                            borderRight: 'none',
                            fontSize: '0.875rem',
                            marginBottom: '2px'
                          }}
                        >
                          {String(hour).padStart(2, '0')}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Minutes */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        height: '100%',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRight: 'none',
                        borderTop: 'none',
                        // borderRadius: '0.25rem',
                        padding: '0.25rem'
                      }}
                    >
                      {minutes.map((minute) => (
                        <div
                          key={minute}
                          onClick={() => setSelectedMinute(minute)}
                          style={{
                            padding: '0.5rem',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedMinute === minute ? '#26397E' : 'transparent',
                            color: selectedMinute === minute ? 'white' : '#333',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem',
                            marginBottom: '2px'
                          }}
                        >
                          {String(minute).padStart(2, '0')}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AM/PM */}
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        height: '100%',
                        border: '1px solid #dee2e6',
                        borderTop: 'none',
                        borderRight: 'none',
                        // borderRadius: '0.25rem',
                        padding: '0.25rem',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      {amPmOptions.map((ampm) => (
                        <div
                          key={ampm}
                          onClick={() => setSelectedAmPm(ampm as 'AM' | 'PM')}
                          style={{
                            padding: '0.5rem',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: selectedAmPm === ampm ? '#26397E' : 'transparent',
                            color: selectedAmPm === ampm ? 'white' : '#333',
                            borderRadius: '0.25rem',
                            fontSize: '0.875rem',
                            marginBottom: '2px'
                          }}
                        >
                          {ampm}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>    
            </div>
            {/* OK Button */}
            <button
                  type="button"
                  onClick={handleOkClick}
                  style={{
                    width: '60px',
                    height: '32px',
                    backgroundColor: '#26397E',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.25rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    marginTop: '0.5rem',
                    float: 'right'
                  }}
                >
                  OK
                </button>
          </div>
        )}
      </div>
      
      <CalendarIcon 
        style={{
          cursor: 'pointer',
          minWidth: '20px',
          height: '20px'
        }} 
        onClick={() => setIsOpen(!isOpen)}
      />
    </div>
  );
};

export default CustomDatePicker; 